import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DeleteOutline, InfoOutlined } from '@mui/icons-material';
import { Box, Chip, GridLegacy as Grid, IconButton, Stack, Switch, TextField, Tooltip } from '@mui/material';

import OfferBox from './OfferBox';
import { OrderListItemWithSortId } from './SortableOfferListOrder';

const OverlayContainerStyles = {
  position: 'relative',
};

interface Props {
  index: number;
  offer: Exclude<OrderListItemWithSortId, { vertical: 'ev' }>;
  onRemove: () => void;
  region: string;
  compact?: boolean;
  showCuration?: boolean;
  onCurationTypeChange?: (curationType: 'fixed' | 'dynamic') => void;
  onEndDateChange?: (endDate: string | null) => void;
}

function SortableOfferBox({
  offer,
  onRemove,
  region,
  index,
  compact = false,
  showCuration = false,
  onCurationTypeChange,
  onEndDateChange,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({
    id: offer.id,
  });

  const style = {
    width: '100%',
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: '2px',
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Grid
      container
      ref={setNodeRef}
      style={style}
      {...attributes}
      alignItems="stretch"
      borderColor="grey.500"
      wrap="nowrap"
    >
      <Grid item xs minWidth={0} ref={setActivatorNodeRef} {...listeners}>
        <Box sx={OverlayContainerStyles}>
          <Box sx={{ opacity: 0.8 }}>
            <OfferBox offer={offer} region={region} index={index} compact={compact} />
          </Box>
        </Box>
      </Grid>
      <Grid
        item
        xs="auto"
        sx={{ display: 'flex', alignItems: compact ? 'flex-start' : 'center', paddingTop: compact ? 1 : 0 }}
      >
        {showCuration && (
          <Stack direction="row" alignItems="center" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <TextField
                type="date"
                size="small"
                label="End date"
                disabled={offer.curationType === 'dynamic'}
                value={offer.endDate ?? ''}
                onChange={(event) => onEndDateChange?.(event.target.value || null)}
                InputLabelProps={{ shrink: true }}
              />
              <Tooltip title="After this Sydney date, the offer is removed from its Fixed position at the next successful refresh. Without an end date, it stays Fixed until you change it. It may return as a Dynamic offer.">
                <IconButton size="small" aria-label="End date explanation">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Chip
                size="small"
                sx={{ width: 80, flexShrink: 0 }}
                color={offer.curationType === 'dynamic' ? 'default' : 'primary'}
                label={offer.curationType === 'dynamic' ? 'Dynamic' : 'Fixed'}
              />
              <Switch
                size="small"
                checked={offer.curationType !== 'dynamic'}
                onChange={(event) => onCurationTypeChange?.(event.target.checked ? 'fixed' : 'dynamic')}
                inputProps={{ 'aria-label': `Keep offer ${offer.bk} fixed on refresh` }}
              />
              <Tooltip
                title={
                  offer.curationType === 'dynamic'
                    ? 'You can move this offer. When new offers arrive, it is removed and may appear again in the new list.'
                    : 'After you save, this offer stays in its current place when new offers arrive. You can still move it.'
                }
              >
                <IconButton
                  size="small"
                  aria-label={`${offer.curationType === 'dynamic' ? 'Dynamic' : 'Fixed'} offer explanation`}
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        )}
        <IconButton size="large" onClick={onRemove}>
          <DeleteOutline />
        </IconButton>
      </Grid>
    </Grid>
  );
}

export default SortableOfferBox;
